import { useEffect, useState } from "react";
import { HealthAppStepType } from "types";
import { errorMessageMap, getAppType, getCWRedirectUrl } from "utils/helper";
import styles from "./NewHealthInsuranceApp.module.css";
import { PageHeader, SystemIcon } from "@justworkshr/milo-components";
import { Alert as MiloAlert } from "@justworkshr/milo-core";
import Stepper from "uicomponents/Stepper";
import {
  STEPS_WITH_COBRA,
  STEPS_WITHOUT_COBRA,
  PAGE_TITLE,
  WC_APP_SUBMITTED_BANNER_TEXT,
} from "./utils/contants";
import PrimaryBusinessAddressModel from "./components/PrimaryBusinessAddress";
import CurrentCoverageModel from "./components/CurrentCoverage";
import CurrentEmployeesModel from "./components/NewCurrentEmployees";
import CurrentInsuranceModel from "./components/CurrentInsurance";
import Congratulations from "./components/Congratulations";
import Loader from "uicomponents/Loader";
import {
  trackFormError,
  trackFormSubmission,
  trackStepperError,
} from "utils/analytics/trackEvent";
import { useUpdateStepMutation } from "types/generated/operations";
import Alert from "uicomponents/Alert";
import { sendCustomerIOEvent } from "utils/analytics/customerio";
import { getMemberUuid } from "utils/idHelpers";
import { useOktaAuth } from "@okta/okta-react";
import CobraCensusModel from "./components/NewCobraCensus";
import {
  HEALTH_STEP_LABELS,
  HEALTH_INSURANCE_STEPS_PLACEHOLDER,
} from "utils/constants";
import { StepDataType, postApplicationStepUpdate } from "utils/stepApiHelpers";
import { getLaunchDarklyFlag } from "app/analytics/launch-darkly";
import { useSearchParams } from "react-router-dom";

interface IHealthInsuranceApplicationProps {
  data?: HealthAppStepType;
  refetchStepData: () => void;
  refetchSteps: () => void;
  stepData?: StepDataType | null;
  disableNextStep?: boolean;
}

const HealthInsuranceApplication = (
  props: IHealthInsuranceApplicationProps
) => {
  let { data, stepData } = props;
  const appType = getAppType();
  const [searchParams] = useSearchParams();
  const isWcAppSubmitted = searchParams.get("wcappsubmitted") === "true";
  const [showAlert, setShowAlert] = useState(true);

  const useNewSteps =
    getLaunchDarklyFlag("cml-use-new-steps") && !!stepData?.steps?.length;

  const disableNextStep =
    props.disableNextStep || !data || data?.applications?.length === 0;

  if (!data || data?.applications?.length === 0) {
    data = {
      applications: [
        {
          uuid: "",
          step: 1,
          status: "IN_PROGRESS",
        },
      ],
    } as HealthAppStepType;
  }

  const hasCobra = data?.healthInsuranceInformation?.cobraUsage;
  let steps = hasCobra ? STEPS_WITH_COBRA : STEPS_WITHOUT_COBRA;

  if (useNewSteps) {
    // stepsData is not null or undefined, it's checked above in useNewSteps logic
    // the ts check doesn't seem to pick that up, so ignoring
    // @ts-ignore
    steps = stepData.steps
      .map((step) => ({
        label: HEALTH_STEP_LABELS[step.step_name] || "",
      }))
      .filter((step) => !!step.label);

    if (steps.length === 0) {
      steps = HEALTH_INSURANCE_STEPS_PLACEHOLDER;
    }

    if (!hasCobra) {
      steps = steps.filter((step) => step.label !== "COBRA partcipants");
    }
  }

  const defaultStep = useNewSteps
    ? stepData?.current_step || 0
    : data?.applications?.[0]?.step || 1;
  const [activeAppStatus, setActiveApp] = useState(defaultStep);
  const [activeStepIndex, setActiveStepIndex] = useState(defaultStep);

  useEffect(() => {
    setActiveApp(defaultStep);
    setActiveStepIndex(defaultStep);
  }, [defaultStep]);

  let currentStep = stepData?.steps[activeStepIndex];

  if (currentStep?.step_name === "cobra_partcipants" && !hasCobra) {
    currentStep = stepData?.steps[activeStepIndex - 1];
  }

  const [updateStep] = useUpdateStepMutation();

  const [disableStepper, setDisableStepper] = useState(false);

  const [stepError, setStepError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submittingApp, setSubmittingApp] = useState(false);

  const { authState } = useOktaAuth();
  const accessToken = authState?.accessToken?.accessToken;

  const nextStep = async (submitApp?: boolean, stepNumber?: number) => {
    try {
      if (!data?.applications?.[0]?.uuid) {
        trackStepperError("uuid not found");
        setStepError(true);
        return;
      }
      if (activeAppStatus === activeStepIndex) {
        if (
          submitApp ||
          (useNewSteps &&
            stepData?.total_steps &&
            stepData?.total_steps > 0 &&
            (activeStepIndex === stepData?.total_steps ||
              (activeStepIndex === stepData?.total_steps - 1 && !hasCobra)))
        ) {
          if (useNewSteps) {
            await postApplicationStepUpdate({
              stepName: currentStep?.step_name || "",
              completed: true,
              token: authState?.accessToken?.accessToken || "",
              appStatus: "completed",
            });

            if (
              stepData?.total_steps &&
              activeStepIndex === stepData?.total_steps - 1 &&
              !hasCobra
            ) {
              await postApplicationStepUpdate({
                stepName: "cobra_partcipants",
                completed: true,
                token: authState?.accessToken?.accessToken || "",
              });
            }
          }
          setSubmittingApp(true);
          if (!useNewSteps) {
            await updateStep({
              variables: {
                uuid: data?.applications?.[0].uuid,
                step: 9,
              },
            });
          }
          trackFormSubmission(9, `${appType} submitted`, 1);
          sendCustomerIOEvent(
            {
              event_name: "health_insurance_application_submitted",
              event_type: "health_app_stages",
              member_uuid: getMemberUuid(),
              data: {
                health_app_status: "HI_COMPLETED",
                app_name: appType,
                version: 1, // TODO: use flowVersion
              },
            },
            accessToken || ""
          );
          setTimeout(function () {
            window.location.href = getCWRedirectUrl(
              "/onboarding_checklist?mdvappsubmitted=true"
            );
          }, 3000);
        } else {
          if (useNewSteps) {
            await postApplicationStepUpdate({
              stepName: currentStep?.step_name || "",
              completed: true,
              token: authState?.accessToken?.accessToken || "",
            });
            await props.refetchSteps();
          } else {
            await updateStep({
              variables: {
                uuid: data?.applications?.[0].uuid,
                step: activeStepIndex + 1,
              },
            });
          }
        }
      } else {
        //if naviagting to a specific step, set active step & send to backend
        if (stepNumber) {
          if (useNewSteps) {
            // - 1 because the new steps uses indexs, so step 1 is index 0
            // and -1 again because the old steps start at 1
            setActiveApp(stepNumber || 1);
            setActiveStepIndex(stepNumber || 1);
          } else {
            await updateStep({
              variables: {
                uuid: data?.applications?.[0].uuid,
                step: stepNumber,
              },
            });
            setActiveApp(stepNumber || 1);
            setActiveStepIndex(stepNumber || 1);
          }
        } else {
          setActiveStepIndex(activeAppStatus || 1);
        }
      }
      setStepError(false);
      props.refetchStepData();
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, `error updating form step- ${error?.message}`);
      setStepError(true);
      return;
    }
  };

  const previousStep = (): void => {
    setActiveStepIndex(activeStepIndex - 1);
  };

  const setDisableStepperValue = (): void => {
    setDisableStepper(true);
  };

  const formList = [
    <PrimaryBusinessAddressModel
      setNextStep={nextStep}
      disableNextStep={disableNextStep}
    />,
    <CurrentCoverageModel
      setNextStep={nextStep}
      setPreviousStep={previousStep}
      setDisableStepperValue={setDisableStepperValue}
    />,
    <CurrentEmployeesModel
      setNextStep={nextStep}
      setPreviousStep={previousStep}
    />,
    <CurrentInsuranceModel
      setNextStep={nextStep}
      setPreviousStep={previousStep}
    />,
    <CobraCensusModel setNextStep={nextStep} setPreviousStep={previousStep} />,
  ];

  const formListByStepName: { [key: string]: any } = {
    primary_address: (
      <PrimaryBusinessAddressModel
        setNextStep={nextStep}
        disableNextStep={disableNextStep}
      />
    ),
    current_coverage: (
      <CurrentCoverageModel
        setNextStep={nextStep}
        setPreviousStep={previousStep}
        setDisableStepperValue={setDisableStepperValue}
      />
    ),
    current_team: (
      <CurrentEmployeesModel
        setNextStep={nextStep}
        setPreviousStep={previousStep}
      />
    ),
    current_insurance_info: (
      <CurrentInsuranceModel
        setNextStep={nextStep}
        setPreviousStep={previousStep}
      />
    ),
    cobra_partcipants: (
      <CobraCensusModel setNextStep={nextStep} setPreviousStep={previousStep} />
    ),
  };

  if (data?.applications?.[0].status === "COMPLETED" && !submittingApp) {
    return <Congratulations />;
  }

  return (
    <div className={styles.container}>
      {isWcAppSubmitted && showAlert && (
        <MiloAlert
          color="additive"
          visible={true}
          dismissible
          onDismiss={() => setShowAlert(false)}
          className={styles.inlineAlert}
        >
          <div className={styles.noticeAlert}>
            <SystemIcon
              iconName="check-circle-inverted"
              size="medium"
              color="success"
            />
            {WC_APP_SUBMITTED_BANNER_TEXT}
          </div>
        </MiloAlert>
      )}
      <div className={styles.titleContainer}>
        <PageHeader
          className={styles.pageHeader}
          title={PAGE_TITLE}
          linkPrevious={
            <a href={getCWRedirectUrl("/onboarding_checklist")}>
              Back to Dashboard
            </a>
          }
        />
      </div>
      {submittingApp ? (
        <Loader message="Submitting your application" />
      ) : (
        <>
          <div className={styles.formContainer}>
            <Stepper
              steps={steps}
              currentStep={activeStepIndex}
              status={activeAppStatus}
              disabled={disableStepper}
              onClick={(stepNumber) => setActiveStepIndex(stepNumber)}
            />
            <div
              className={`${styles.formDiv} ApplicationStep${activeStepIndex}`}
            >
              {useNewSteps
                ? formListByStepName[currentStep?.step_name || ""] || <Loader />
                : formList[activeStepIndex - 1]}
            </div>
          </div>
          {stepError && <Alert message={errorMessage} />}
        </>
      )}
    </div>
  );
};

export default HealthInsuranceApplication;
